import { generateMockSpace } from '@placeos/events';
import { MOCK_BUILDINGS, MOCK_REGIONS } from './zone.data';

// ── UCLA Space Types ────────────────────────────────────────────────

const SPACE_TYPES = [
    'Ballroom',
    'Auditorium',
    'Meeting Room',
    'Conference Room',
    'Boardroom',
    'Event Space',
    'Outdoor Space',
    'Dining Venue',
    'Gallery',
    'Theater',
    'Classroom',
];

// ── Features by room type (drawn from UCLA service offerings) ───────

const ROOM_FEATURES = {
    Ballroom: [
        'Audio System',
        'Projector',
        'Stage/Platform',
        'Flexible Seating',
        'Catering Setup',
        'Dance Floor',
    ],
    Auditorium: [
        'Audio System',
        'Projector',
        'Stage/Platform',
        'Tiered Seating',
        'Lighting Control',
        'Video Conference',
    ],
    'Meeting Room': [
        'Display Screen',
        'Video Conference',
        'Whiteboard',
        'Phone',
    ],
    'Conference Room': [
        'Projector',
        'Video Conference',
        'Whiteboard',
        'Audio System',
        'Phone',
    ],
    Boardroom: [
        'Large Display',
        'Video Conference',
        'Audio System',
        'HDMI',
        'Executive Seating',
    ],
    'Event Space': [
        'Audio System',
        'Projector',
        'Flexible Seating',
        'Catering Setup',
        'WiFi',
    ],
    'Outdoor Space': [
        'Flexible Seating',
        'Catering Setup',
        'Portable PA',
        'Natural Light',
    ],
    'Dining Venue': [
        'Catering Setup',
        'Bar Service',
        'Audio System',
        'Piano',
    ],
    Gallery: [
        'Projector',
        'Audio System',
        'Lighting Control',
        'Exhibition Walls',
    ],
    Theater: [
        'Stage/Platform',
        'Audio System',
        'Stage Lighting',
        'Tiered Seating',
        'Green Room',
    ],
    Classroom: [
        'Projector',
        'Whiteboard',
        'Audio System',
        'Video Conference',
    ],
};

// ── Helper to map floor index to level code used in zone.data.ts ────

const floorIndexToLevelCode = (floor_index: number): string => {
    if (floor_index === 0) return 'G';
    return String(floor_index);
};

// ── Helper to get zone hierarchy ────────────────────────────────────

const getZoneHierarchy = (buildingId: string, levelId: string) => {
    const building = MOCK_BUILDINGS.find((b) => b.id === buildingId);
    if (!building) return ['zone-org', 'region-ucla-campus', buildingId, levelId];
    const region = MOCK_REGIONS.find((r) => r.id === building.parent_id);
    const orgId = region ? region.parent_id : 'zone-org';
    return [orgId, building.parent_id, buildingId, levelId];
};

// ── UCLA Room Definitions (from actual UCLA venue data) ─────────────

interface UclaRoom {
    name: string;
    display_name: string;
    type: string;
    building_id: string;
    building_name: string;
    floor: string;
    floor_index: number;
    capacity: number;
    sqft: number;
    features: string[];
    services: string[];
    hourly_rate: number;
    half_day_rate?: number;
    full_day_rate?: number;
    approval_required: boolean;
    catering_available: boolean;
    notes?: string;
}

const UCLA_ROOMS: UclaRoom[] = [
    // ═══════════════════════════════════════════════════════════════════
    // COVEL COMMONS (bld-01) — from Meeting Room Rental Rates + Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Grand Horizon & Terrace',
        display_name: 'Grand Horizon & Terrace',
        type: 'Event Space',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 350,
        sqft: 5900,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'Terrace Access', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators', 'On-site Parking'],
        hourly_rate: 450,
        half_day_rate: 3000,
        full_day_rate: 3600,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Salon A & Terrace',
        display_name: 'Salon A & Terrace',
        type: 'Event Space',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'Terrace Access', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators', 'On-site Parking'],
        hourly_rate: 400,
        half_day_rate: 2750,
        full_day_rate: 3150,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'South Bay Room',
        display_name: 'South Bay Room',
        type: 'Meeting Room',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 960,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 130,
        half_day_rate: 900,
        full_day_rate: 1050,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'West Coast',
        display_name: 'West Coast',
        type: 'Meeting Room',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 55,
        sqft: 920,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 130,
        half_day_rate: 900,
        full_day_rate: 1050,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'North Ridge',
        display_name: 'North Ridge',
        type: 'Meeting Room',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 50,
        sqft: 874,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 130,
        half_day_rate: 900,
        full_day_rate: 1050,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Conference Room L-04',
        display_name: 'Conference Room L-04',
        type: 'Conference Room',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 20,
        sqft: 400,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'Phone', 'WiFi'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi'],
        hourly_rate: 50,
        half_day_rate: 250,
        full_day_rate: 400,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Conference Room 207',
        display_name: 'Conference Room 207',
        type: 'Conference Room',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 20,
        sqft: 400,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'Phone', 'WiFi'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi'],
        hourly_rate: 50,
        half_day_rate: 250,
        full_day_rate: 400,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Northwest Campus Auditorium',
        display_name: 'Northwest Campus Auditorium',
        type: 'Auditorium',
        building_id: 'bld-01',
        building_name: 'Covel Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 349,
        sqft: 4500,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Lighting Control', 'Video Conference'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators', 'On-site Parking'],
        hourly_rate: 300,
        half_day_rate: 2000,
        full_day_rate: 2400,
        approval_required: true,
        catering_available: false,
        notes: 'Teleconferences, lectures, presentations, forums, small theatrical performances',
    },

    // ═══════════════════════════════════════════════════════════════════
    // CARNESALE COMMONS (bld-02) — from Meeting Room Rental Rates + Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Palisades Ballroom',
        display_name: 'Palisades Ballroom',
        type: 'Ballroom',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 450,
        sqft: 6700,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Flexible Seating', 'Catering Setup', 'Dance Floor'],
        services: ['Full Catering Services', 'Business Center', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators & Conference Managers', 'On-site Parking', 'Event Material Storage', 'Signage & Displays'],
        hourly_rate: 450,
        half_day_rate: 3000,
        full_day_rate: 3600,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Palisades ABCF',
        display_name: 'Palisades ABCF Section',
        type: 'Event Space',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 300,
        sqft: 4500,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators & Conference Managers', 'On-site Parking'],
        hourly_rate: 420,
        half_day_rate: 2900,
        full_day_rate: 3400,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Palisades DE',
        display_name: 'Palisades DE Section',
        type: 'Event Space',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2250,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 300,
        half_day_rate: 2100,
        full_day_rate: 2600,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Malibu',
        display_name: 'Malibu',
        type: 'Meeting Room',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 40,
        sqft: 713,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 110,
        half_day_rate: 750,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Venice A & B',
        display_name: 'Venice A & B',
        type: 'Meeting Room',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 70,
        sqft: 1211,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 140,
        half_day_rate: 1000,
        full_day_rate: 1150,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Hermosa A & B',
        display_name: 'Hermosa A & B',
        type: 'Meeting Room',
        building_id: 'bld-02',
        building_name: 'Carnesale Commons',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 70,
        sqft: 1205,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 140,
        half_day_rate: 1000,
        full_day_rate: 1150,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // DE NEVE PLAZA (bld-03) — from Meeting Room Rental Rates + Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Plaza Room',
        display_name: 'Plaza Room',
        type: 'Event Space',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 250,
        sqft: 3700,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators & Conference Managers', 'On-site Parking'],
        hourly_rate: 300,
        half_day_rate: 2100,
        full_day_rate: 2400,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Plaza A',
        display_name: 'Plaza A',
        type: 'Conference Room',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 120,
        sqft: 1850,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 190,
        half_day_rate: 1300,
        full_day_rate: 1700,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Plaza B',
        display_name: 'Plaza B',
        type: 'Conference Room',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 120,
        sqft: 1850,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Full Catering Services', 'Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'On-site Parking'],
        hourly_rate: 190,
        half_day_rate: 1300,
        full_day_rate: 1700,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Sycamore',
        display_name: 'Sycamore',
        type: 'Meeting Room',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 30,
        sqft: 510,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi'],
        hourly_rate: 60,
        half_day_rate: 400,
        full_day_rate: 550,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Magnolia',
        display_name: 'Magnolia',
        type: 'Meeting Room',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 12,
        sqft: 200,
        features: ['Display Screen', 'Video Conference', 'WiFi'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi'],
        hourly_rate: 30,
        half_day_rate: 210,
        full_day_rate: 315,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Jacaranda',
        display_name: 'Jacaranda',
        type: 'Meeting Room',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 12,
        sqft: 200,
        features: ['Display Screen', 'Video Conference', 'WiFi'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi'],
        hourly_rate: 30,
        half_day_rate: 210,
        full_day_rate: 315,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'De Neve Lecture Auditorium',
        display_name: 'De Neve Lecture Auditorium',
        type: 'Auditorium',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 430,
        sqft: 5000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Lighting Control'],
        services: ['Audio-Visual Tech & Support', 'Complimentary Wi-Fi', 'Event Facilitators & Conference Managers', 'On-site Parking'],
        hourly_rate: 300,
        half_day_rate: 2000,
        full_day_rate: 2400,
        approval_required: true,
        catering_available: false,
        notes: 'Catering only available in Plaza Room, NOT Auditorium',
    },

    // ═══════════════════════════════════════════════════════════════════
    // UNIVERSITY CLUB (bld-04) — from University Club Rental Rates + Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Cederbaum Board Room',
        display_name: 'Cederbaum Board Room',
        type: 'Boardroom',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 12,
        sqft: 170,
        features: ['Large Display', 'HDMI', 'Executive Seating', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi'],
        hourly_rate: 50,
        half_day_rate: 250,
        full_day_rate: 250,
        approval_required: false,
        catering_available: true,
        notes: '40" TV w/HDMI cord. Min occupancy 10.',
    },
    {
        name: 'Sierra',
        display_name: 'Sierra',
        type: 'Conference Room',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 50,
        sqft: 806,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services', 'Custom Menu Development'],
        hourly_rate: 85,
        half_day_rate: 500,
        full_day_rate: 500,
        approval_required: false,
        catering_available: true,
        notes: 'No screen in room. Min occupancy 20. Configs: Reception 40, Theatre 50, Rounds 40, Classroom 25, Board 24.',
    },
    {
        name: 'Hacienda',
        display_name: 'Hacienda',
        type: 'Conference Room',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 999,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services', 'Custom Menu Development'],
        hourly_rate: 170,
        half_day_rate: 1000,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
        notes: 'Min occupancy 40. Configs: Reception 60, Theatre 80, Rounds 60, Classroom 35, Board 34.',
    },
    {
        name: 'Redwood',
        display_name: 'Redwood',
        type: 'Conference Room',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 620,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services'],
        hourly_rate: 170,
        half_day_rate: 1000,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
        notes: 'Entire room. Min occupancy 40. Can be split into Redwood A (West w/screen) and Redwood B (East).',
    },
    {
        name: 'Sequoia',
        display_name: 'Sequoia',
        type: 'Event Space',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 1535,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'Patio Access', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services', 'Custom Menu Development', 'Setup & Clean-up Crews'],
        hourly_rate: 210,
        half_day_rate: 1250,
        full_day_rate: 1250,
        approval_required: true,
        catering_available: true,
        notes: 'Includes Patio. Min occupancy 50. Configs: Reception 80, Theatre 100, Rounds 60, Classroom 50, Board 32.',
    },
    {
        name: 'Morrison Room',
        display_name: 'Morrison Room (incl. Terrace)',
        type: 'Event Space',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 200,
        sqft: 2252,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Flexible Seating', 'Catering Setup', 'Piano', 'Terrace Access', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services', 'Piano Use ($250)', 'Professional Event Coordination', 'Custom Menu Development', 'Setup & Clean-up Crews'],
        hourly_rate: 340,
        half_day_rate: 2000,
        full_day_rate: 2000,
        approval_required: true,
        catering_available: true,
        notes: 'Includes Terrace. Piano Use $250, Piano Tuning $200. Min occupancy 80. Configs: Reception 200, Theatre 200, Rounds 180, Classroom 112, Board 50.',
    },
    {
        name: 'Main Dining Room',
        display_name: 'Main Dining Room',
        type: 'Dining Venue',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 400,
        sqft: 4191,
        features: ['Audio System', 'Catering Setup', 'Flexible Seating', 'Bar Service', 'WiFi'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Complimentary Guest WiFi', 'Bar & Cocktail Services', 'Professional Event Coordination', 'Custom Menu Development', 'Setup & Clean-up Crews'],
        hourly_rate: 750,
        half_day_rate: 4400,
        full_day_rate: 4400,
        approval_required: true,
        catering_available: true,
        notes: '$8,000 weekday buy-out displaces Member Lunch. Min occupancy 175. Configs: Reception 350, Theatre 400, Rounds 200.',
    },
    {
        name: 'Sherie Bar & Lounge',
        display_name: 'Sherie Bar & Lounge',
        type: 'Dining Venue',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 1886,
        features: ['Audio System', 'Bar Service', 'Piano', 'Flexible Seating', 'WiFi'],
        services: ['Full-Service Catering', 'Bar & Cocktail Services', 'Piano Use ($250)', 'Audio-Visual Support', 'Complimentary Guest WiFi'],
        hourly_rate: 210,
        half_day_rate: 1250,
        full_day_rate: 1250,
        approval_required: false,
        catering_available: true,
        notes: 'Piano Use $250, Piano Tuning $200. Min occupancy 50. Configs: Reception 50, Theatre 100, Rounds 60, Board 24.',
    },
    {
        name: 'West Patio',
        display_name: 'West Patio',
        type: 'Outdoor Space',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 1044,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Full-Service Catering', 'Setup & Clean-up Crews'],
        hourly_rate: 105,
        half_day_rate: 625,
        full_day_rate: 625,
        approval_required: false,
        catering_available: true,
        notes: 'Min occupancy 25. Configs: Reception 40, Rounds 40, Board 24.',
    },
    {
        name: 'Garden Patio',
        display_name: 'Garden Patio',
        type: 'Outdoor Space',
        building_id: 'bld-04',
        building_name: 'University Club',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 2160,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Full-Service Catering', 'Audio-Visual Support', 'Setup & Clean-up Crews'],
        hourly_rate: 420,
        half_day_rate: 2500,
        full_day_rate: 2500,
        approval_required: true,
        catering_available: true,
        notes: '$2,500 after 4 p.m. or weekends. $5,500 weekday buy-out. Configs: Reception 200, Theatre 100, Rounds 100.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // WEYBURN TERRACE (part of bld-03, mapped to De Neve area)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Village View Room',
        display_name: 'Village View Room',
        type: 'Event Space',
        building_id: 'bld-03',
        building_name: 'De Neve Plaza',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 100,
        sqft: 1800,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Kitchenette', 'Terrace Access', 'WiFi'],
        services: ['Audio-Visual Support', 'Sound System', 'Room Setup & Striking', 'Catering Permitted', 'Custodial Services', 'UCLA Guest WiFi'],
        hourly_rate: 200,
        half_day_rate: 1400,
        full_day_rate: 1800,
        approval_required: false,
        catering_available: true,
        notes: 'Outdoor terrace with city views. Kitchenette for food prep.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // LUSKIN CONFERENCE CENTER (bld-05) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Centennial Ballroom',
        display_name: 'Centennial Ballroom',
        type: 'Ballroom',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 500,
        sqft: 10000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Flexible Seating', 'Catering Setup', 'Dance Floor', 'Smart Room Technology', 'Lighting Control'],
        services: ['On-Site Event Catering', 'Dedicated Conference Services Planners', 'Smart Room Technology', 'Built-in Projection & Motorized Screens', 'Sound Amplification & Microphones', 'Video Conferencing', 'Simultaneous Translation', 'On-Site Technical Staff', 'Nutrition Hubs'],
        hourly_rate: 750,
        half_day_rate: 5000,
        full_day_rate: 8000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Centennial Terrace',
        display_name: 'Centennial Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 250,
        sqft: 5000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['On-Site Event Catering', 'Dedicated Conference Services Planners', 'Portable AV Equipment'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Laureate Room',
        display_name: 'Laureate Room',
        type: 'Conference Room',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 80,
        sqft: 1800,
        features: ['Audio System', 'Projector', 'Video Conference', 'Smart Room Technology', 'WiFi'],
        services: ['On-Site Event Catering', 'Smart Room Technology', 'Built-in Projection', 'Video Conferencing', 'On-Site Technical Staff', 'Nutrition Hubs'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Luskin Meeting Room 1',
        display_name: 'Meeting Room 1',
        type: 'Meeting Room',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 30,
        sqft: 600,
        features: ['Display Screen', 'Video Conference', 'Smart Room Technology', 'WiFi'],
        services: ['On-Site Event Catering', 'Smart Room Technology', 'Video Conferencing', 'Complimentary WiFi'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Luskin Meeting Room 2',
        display_name: 'Meeting Room 2',
        type: 'Meeting Room',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 25,
        sqft: 500,
        features: ['Display Screen', 'Video Conference', 'Smart Room Technology', 'WiFi'],
        services: ['On-Site Event Catering', 'Smart Room Technology', 'Video Conferencing', 'Complimentary WiFi'],
        hourly_rate: 90,
        half_day_rate: 550,
        full_day_rate: 900,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Luskin Meeting Room 3',
        display_name: 'Meeting Room 3',
        type: 'Meeting Room',
        building_id: 'bld-05',
        building_name: 'Luskin Conference Center',
        floor: 'Level 2',
        floor_index: 2,
        capacity: 20,
        sqft: 450,
        features: ['Display Screen', 'Video Conference', 'Smart Room Technology', 'WiFi'],
        services: ['On-Site Event Catering', 'Smart Room Technology', 'Video Conferencing', 'Complimentary WiFi'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // ASUCLA EVENT SPACES (bld-06) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Ackerman Grand Ballroom',
        display_name: 'Ackerman Grand Ballroom',
        type: 'Ballroom',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 1200,
        sqft: 12000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Flexible Seating', 'Catering Setup', 'Dance Floor', 'Lighting Control'],
        services: ['Full-Service Catering (ASUCLA)', 'Audio-Visual Support', 'Custom Room Setup & Furniture', 'Event Planning & Coordination', 'Digital Signage Promotion', 'Loading Dock Access', 'Cleaning & Custodial Support', 'Security Coordination'],
        hourly_rate: 800,
        half_day_rate: 5000,
        full_day_rate: 8000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Bruin Reception Room',
        display_name: 'Bruin Reception Room',
        type: 'Event Space',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1200,
        features: ['Audio System', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full-Service Catering (ASUCLA)', 'Audio-Visual Support', 'Custom Room Setup', 'Event Planning & Coordination'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Kerckhoff Grand Salon',
        display_name: 'Charles E. Young Grand Salon',
        type: 'Event Space',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 200,
        sqft: 3500,
        features: ['Audio System', 'Projector', 'Vaulted Ceiling', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Full-Service Catering (ASUCLA)', 'Audio-Visual Support', 'Custom Room Setup & Furniture', 'Event Planning & Coordination', 'Digital Signage Promotion'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
        notes: 'Historic vaulted-ceiling venue in Kerckhoff Hall.',
    },
    {
        name: 'Viewpoint Conference Room',
        display_name: 'Viewpoint Conference Room',
        type: 'Conference Room',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 40,
        sqft: 700,
        features: ['Display Screen', 'Video Conference', 'Panoramic Views', 'WiFi'],
        services: ['Audio-Visual Support', 'Complimentary WiFi', 'Catering Available'],
        hourly_rate: 120,
        half_day_rate: 700,
        full_day_rate: 1200,
        approval_required: false,
        catering_available: true,
        notes: 'Top-floor views of campus.',
    },
    {
        name: 'Kerckhoff State Room A',
        display_name: 'Kerckhoff State Room A',
        type: 'Meeting Room',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 25,
        sqft: 400,
        features: ['Display Screen', 'Video Conference', 'WiFi'],
        services: ['Audio-Visual Support', 'Complimentary WiFi', 'Catering Available'],
        hourly_rate: 75,
        half_day_rate: 450,
        full_day_rate: 750,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Bruin Plaza',
        display_name: 'Bruin Plaza',
        type: 'Outdoor Space',
        building_id: 'bld-06',
        building_name: 'ASUCLA Event Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 500,
        sqft: 8000,
        features: ['Portable PA', 'Flexible Seating', 'Natural Light', 'Catering Setup'],
        services: ['Full-Service Catering (ASUCLA)', 'Audio-Visual Support', 'Event Planning & Coordination', 'Security Coordination'],
        hourly_rate: 500,
        half_day_rate: 3000,
        full_day_rate: 5000,
        approval_required: true,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // ANDERSON SCHOOL OF MANAGEMENT (bld-07) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Korn Convocation Hall',
        display_name: 'Carolbeth and Lester Korn Convocation Hall',
        type: 'Auditorium',
        building_id: 'bld-07',
        building_name: 'Anderson School of Management',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 300,
        sqft: 5000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Lighting Control', 'Video Conference'],
        services: ['Venue Rentals', 'Audio-Visual Equipment & Support', 'High-Speed WiFi', 'Catering Coordination', 'Event Planning & Management', 'Visitor Parking & Transportation', 'Safety & Security Liaison', 'Virtual & Hybrid Presentation Support', 'Ticketing Services', 'Disability & Accessibility Accommodations'],
        hourly_rate: 500,
        half_day_rate: 3000,
        full_day_rate: 5000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Korn Foyer',
        display_name: 'Carolbeth and Lester Korn Foyer',
        type: 'Event Space',
        building_id: 'bld-07',
        building_name: 'Anderson School of Management',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'WiFi'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning & Management', 'High-Speed WiFi'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Executive Dining Room',
        display_name: 'Executive Dining Room',
        type: 'Dining Venue',
        building_id: 'bld-07',
        building_name: 'Anderson School of Management',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 40,
        sqft: 800,
        features: ['Audio System', 'Catering Setup', 'Executive Seating', 'WiFi'],
        services: ['Venue Rentals', 'Catering Coordination', 'High-Speed WiFi', 'Disability & Accessibility Accommodations'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Briskin Family Plaza',
        display_name: 'Judy & Bernard Briskin Family Plaza',
        type: 'Outdoor Space',
        building_id: 'bld-07',
        building_name: 'Anderson School of Management',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 4000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning & Management', 'Safety & Security Liaison'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Anderson Large Classroom',
        display_name: 'Anderson Large Classroom',
        type: 'Classroom',
        building_id: 'bld-07',
        building_name: 'Anderson School of Management',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 80,
        sqft: 1500,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'Audio-Visual Equipment & Support', 'High-Speed WiFi', 'Virtual & Hybrid Presentation Support'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
    },

    // ═══════════════════════════════════════════════════════════════════
    // FOWLER MUSEUM (bld-08) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Lenart Auditorium',
        display_name: 'Harry and Yvonne Lenart Auditorium',
        type: 'Auditorium',
        building_id: 'bld-08',
        building_name: 'Fowler Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 324,
        sqft: 4500,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Lighting Control'],
        services: ['Venue Rentals', 'Audio-Visual Equipment', 'Campus Guest WiFi', 'Catering Coordination', 'Event Planning & Management', 'Technical Support', 'Security & Safety Reviews', 'Custodial & Clean-up', 'Photography & Rights Reproductions'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Goldenberg Terrace',
        display_name: 'Barbara and Joseph Goldenberg Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-08',
        building_name: 'Fowler Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 250,
        sqft: 3500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning & Management', 'Photography Permitting'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Davis Courtyard',
        display_name: 'Elizabeth and W. Thomas Davis Courtyard',
        type: 'Outdoor Space',
        building_id: 'bld-08',
        building_name: 'Fowler Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning & Management'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Deutsch Seminar Room',
        display_name: 'Eleanor Deutsch Seminar Room',
        type: 'Meeting Room',
        building_id: 'bld-08',
        building_name: 'Fowler Museum',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 30,
        sqft: 500,
        features: ['Projector', 'Audio System', 'Video Conference', 'WiFi'],
        services: ['Venue Rentals', 'Audio-Visual Equipment', 'Campus Guest WiFi'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'UCLA Art Council Amphitheater',
        display_name: 'UCLA Art Council Amphitheater',
        type: 'Outdoor Space',
        building_id: 'bld-08',
        building_name: 'Fowler Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Stage/Platform', 'Tiered Seating', 'Natural Light', 'Portable PA'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning & Management', 'Photography Permitting'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: true,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // ROYCE HALL (bld-09) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Royce Hall Auditorium',
        display_name: 'Royce Hall Main Auditorium',
        type: 'Auditorium',
        building_id: 'bld-09',
        building_name: 'Royce Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 1834,
        sqft: 20000,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Green Room', 'Dressing Rooms', 'Lighting Control'],
        services: ['Professional Stage Crew & Management', 'House Staff & Ushers', 'UCLA Central Ticket Office', 'Full Lighting & Sound Production', 'Event Planning & Logistics', 'Catering Coordination', 'Filming & Photography Permitting', 'Security & Fire Marshal Liaison'],
        hourly_rate: 1500,
        half_day_rate: 8000,
        full_day_rate: 15000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Humanities Conference Room',
        display_name: 'Humanities Conference Room',
        type: 'Conference Room',
        building_id: 'bld-09',
        building_name: 'Royce Hall',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 120,
        sqft: 2000,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Audio-Visual Support', 'Catering Coordination', 'Kitchenette Access', 'Guest WiFi'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Herbert Morris Seminar Room',
        display_name: 'Herbert Morris Seminar Room',
        type: 'Meeting Room',
        building_id: 'bld-09',
        building_name: 'Royce Hall',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 80,
        sqft: 1200,
        features: ['Audio System', 'Projector', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Audio-Visual Support', 'Catering Coordination', 'Kitchenette Access', 'Guest WiFi'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Royce West Terrace',
        display_name: 'Royce West Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-09',
        building_name: 'Royce Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Catering Coordination', 'Event Planning & Logistics', 'Security Coordination'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // JAMES WEST ALUMNI CENTER (bld-10) — from Event Spaces
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Collins Alumni Conference Room',
        display_name: 'Collins Alumni Conference Room',
        type: 'Conference Room',
        building_id: 'bld-10',
        building_name: 'James West Alumni Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2753,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'Patio Access', 'WiFi'],
        services: ['Venue Rentals', 'Catering Management', 'Event Staffing', 'Room Setup & Strike', 'Integrated Sound Systems', 'ADA Accessible', 'Guest WiFi'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: false,
        catering_available: true,
        notes: 'Two-tiered interior. Supports up to six sound inputs.',
    },
    {
        name: 'Tom Davis Founders Room',
        display_name: 'Tom Davis Founders\' Room',
        type: 'Event Space',
        building_id: 'bld-10',
        building_name: 'James West Alumni Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1678,
        features: ['Audio System', 'Flexible Seating', 'Catering Setup', 'Patio Access', 'WiFi'],
        services: ['Venue Rentals', 'Catering Management', 'Event Staffing', 'Room Setup & Strike', 'Guest WiFi'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Robert Kerr Galleria',
        display_name: 'Robert Kerr Galleria',
        type: 'Gallery',
        building_id: 'bld-10',
        building_name: 'James West Alumni Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 250,
        sqft: 4434,
        features: ['Exhibition Walls', 'Flexible Seating', 'Catering Setup', 'Natural Light', 'WiFi'],
        services: ['Venue Rentals', 'Catering Management', 'Event Staffing', 'Room Setup & Strike', 'Guest WiFi', 'ADA Accessible'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
        notes: 'Known as the "Alumni Living Room". Available after business hours or weekends only.',
    },
    {
        name: 'Alumni Center Northwest Patio',
        display_name: 'Northwest Patio',
        type: 'Outdoor Space',
        building_id: 'bld-10',
        building_name: 'James West Alumni Center',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 5000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Fountain'],
        services: ['Venue Rentals', 'Catering Management', 'Event Staffing'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: false,
        catering_available: true,
        notes: '1st floor patio with lush trees and fountains. Part of 11,335 sq ft total outdoor space.',
    },
    {
        name: 'Alumni Center Northeast Patio',
        display_name: 'Northeast Patio',
        type: 'Outdoor Space',
        building_id: 'bld-10',
        building_name: 'James West Alumni Center',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 100,
        sqft: 3000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Management', 'Event Staffing'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
        notes: '2nd floor patio.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // HAMMER MUSEUM (bld-11)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Billy Wilder Theater',
        display_name: 'Billy Wilder Theater',
        type: 'Theater',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 300,
        sqft: 4500,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Lighting Control'],
        services: ['AV Support', 'Event Planning & Staffing', 'Catering Coordination', 'Virtual & Hybrid Support', 'Group Tours'],
        hourly_rate: 600,
        half_day_rate: 3500,
        full_day_rate: 6000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Pritzker Family Commons',
        display_name: 'Pritzker Family Commons & South Courtyard',
        type: 'Event Space',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 400,
        sqft: 6000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'WiFi'],
        services: ['AV Support', 'Catering Coordination', 'Event Planning & Staffing', 'Building Engineering & Custodial'],
        hourly_rate: 500,
        half_day_rate: 3000,
        full_day_rate: 5000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Annenberg Terrace',
        display_name: 'Annenberg Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 200,
        sqft: 3000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Catering Coordination', 'Event Planning & Staffing', 'Building Engineering & Custodial'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'McMorrow Terrace',
        display_name: 'McMorrow Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 150,
        sqft: 2500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Catering Coordination', 'Event Planning & Staffing'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Hammer Executive Board Room',
        display_name: 'Executive Board Room',
        type: 'Boardroom',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 20,
        sqft: 400,
        features: ['Large Display', 'Video Conference', 'Audio System', 'WiFi'],
        services: ['AV Support', 'Complimentary WiFi'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'The Lab',
        display_name: 'The Lab',
        type: 'Event Space',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1000,
        features: ['Flexible Seating', 'Audio System', 'Projector', 'WiFi'],
        services: ['AV Support', 'Catering Coordination', 'Event Planning & Staffing'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
        notes: 'Flexible workshop and event space.',
    },
    {
        name: 'Nimoy Studio',
        display_name: 'Bay Nimoy Studio',
        type: 'Event Space',
        building_id: 'bld-11',
        building_name: 'Hammer Museum',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1200,
        features: ['Flexible Seating', 'Audio System', 'Stage/Platform', 'Lighting Control', 'WiFi'],
        services: ['AV Support', 'Catering Coordination', 'Event Planning & Staffing'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // SCHOENBERG MUSIC BUILDING (bld-12)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Schoenberg Hall',
        display_name: 'Schoenberg Hall',
        type: 'Auditorium',
        building_id: 'bld-12',
        building_name: 'Schoenberg Music Building',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 522,
        sqft: 7000,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Green Room', 'Lighting Control'],
        services: ['Venue Rentals', 'AV Support', 'Live-Streaming & Recording', 'Catering Coordination', 'Event Staffing'],
        hourly_rate: 800,
        half_day_rate: 4500,
        full_day_rate: 7500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Lani Hall',
        display_name: 'Lani Hall Theater',
        type: 'Theater',
        building_id: 'bld-12',
        building_name: 'Schoenberg Music Building',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 133,
        sqft: 2000,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Lighting Control'],
        services: ['Venue Rentals', 'AV Support', 'Live-Streaming & Recording', 'Event Staffing'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: true,
        catering_available: false,
        notes: 'Recital hall for small groups and lectures.',
    },
    {
        name: 'Jan Popper Theater',
        display_name: 'Jan Popper Theater',
        type: 'Theater',
        building_id: 'bld-12',
        building_name: 'Schoenberg Music Building',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 140,
        sqft: 2200,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Lighting Control'],
        services: ['Venue Rentals', 'AV Support', 'Event Staffing'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: true,
        catering_available: false,
        notes: 'Intimate performance venue.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // THEATER, FILM & TELEVISION (bld-13)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Freud Playhouse',
        display_name: 'Freud Playhouse',
        type: 'Theater',
        building_id: 'bld-13',
        building_name: 'Theater, Film & Television',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 562,
        sqft: 8000,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Green Room', 'Dressing Rooms'],
        services: ['Venue Rentals', 'Production & Technical Support', 'Film Permitting', 'Ticketing Services', 'Catering Coordination'],
        hourly_rate: 1000,
        half_day_rate: 6000,
        full_day_rate: 10000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'James Bridges Theater',
        display_name: 'James Bridges Theater (Melnitz 1409)',
        type: 'Theater',
        building_id: 'bld-13',
        building_name: 'Theater, Film & Television',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 278,
        sqft: 4000,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating', 'Projector'],
        services: ['Venue Rentals', 'Production & Technical Support', 'Film Permitting', 'Ticketing Services', 'Catering Coordination'],
        hourly_rate: 500,
        half_day_rate: 3000,
        full_day_rate: 5000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Little Theater',
        display_name: 'Little Theater',
        type: 'Theater',
        building_id: 'bld-13',
        building_name: 'Theater, Film & Television',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 172,
        sqft: 2500,
        features: ['Audio System', 'Stage/Platform', 'Stage Lighting', 'Tiered Seating'],
        services: ['Venue Rentals', 'Production & Technical Support', 'AV Orientation'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: false,
    },
    {
        name: 'Studio Theater 1340',
        display_name: 'Studio Theater 1340',
        type: 'Theater',
        building_id: 'bld-13',
        building_name: 'Theater, Film & Television',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 81,
        sqft: 1200,
        features: ['Flexible Seating', 'Stage/Platform', 'Lighting Control', 'Audio System'],
        services: ['Venue Rentals', 'Production & Technical Support'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: false,
        notes: 'Flexible black box theater.',
    },
    {
        name: 'Darren Star Screening Room',
        display_name: 'Darren Star Screening Room',
        type: 'Theater',
        building_id: 'bld-13',
        building_name: 'Theater, Film & Television',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 60,
        sqft: 900,
        features: ['Projector', 'Audio System', 'Tiered Seating', 'Lighting Control'],
        services: ['Venue Rentals', 'AV Support', 'Film Permitting'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
        notes: 'Screening and workshop space.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // CNSI (bld-14)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'CNSI Auditorium',
        display_name: 'CNSI Auditorium',
        type: 'Auditorium',
        building_id: 'bld-14',
        building_name: 'CNSI',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 260,
        sqft: 4000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Video Conference'],
        services: ['AV Tech & Support', 'Complimentary WiFi', 'Event Furniture'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'CNSI Lobby Space',
        display_name: 'CNSI Lobby',
        type: 'Event Space',
        building_id: 'bld-14',
        building_name: 'CNSI',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Flexible Seating', 'Natural Light', 'WiFi', 'Catering Setup'],
        services: ['AV Tech & Support', 'Complimentary WiFi', 'Event Furniture'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'CNSI Multipurpose Room',
        display_name: 'CNSI Multipurpose Meeting Space',
        type: 'Conference Room',
        building_id: 'bld-14',
        building_name: 'CNSI',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 60,
        sqft: 1000,
        features: ['Projector', 'Video Conference', 'Audio System', 'Natural Light', 'WiFi'],
        services: ['AV Tech & Support', 'Complimentary WiFi'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
        notes: 'Overlooks Court of Sciences through floor-to-ceiling windows.',
    },
    {
        name: 'CNSI Conference Room A',
        display_name: 'CNSI Conference Room A',
        type: 'Conference Room',
        building_id: 'bld-14',
        building_name: 'CNSI',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 20,
        sqft: 400,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['AV Tech & Support', 'Complimentary WiFi'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'CNSI Terrace',
        display_name: 'CNSI Upper Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-14',
        building_name: 'CNSI',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 100,
        sqft: 2000,
        features: ['Flexible Seating', 'Natural Light', 'Portable PA'],
        services: ['AV Tech & Support', 'Catering Coordination'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // BOTANICAL GARDENS (bld-15)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'La Kretz Garden Pavilion',
        display_name: 'La Kretz Garden Pavilion',
        type: 'Event Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 1500,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Natural Light', 'WiFi'],
        services: ['Venue Rentals', 'AV Setup', 'Catering Coordination', 'Photography Permitting', 'Educational Workshops'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Herbert Plaza',
        display_name: 'Herbert Plaza',
        type: 'Outdoor Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 120,
        sqft: 2000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination', 'Photography Permitting'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
        notes: 'Add-on space to La Kretz Pavilion.',
    },
    {
        name: 'Parks Patio',
        display_name: "Park's Patio",
        type: 'Outdoor Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Jewell Terrace',
        display_name: 'Jewell Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1200,
        features: ['Flexible Seating', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 120,
        half_day_rate: 700,
        full_day_rate: 1200,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Norris Terrace',
        display_name: 'Norris Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1200,
        features: ['Flexible Seating', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 120,
        half_day_rate: 700,
        full_day_rate: 1200,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Garden Amphitheater',
        display_name: 'Outdoor Amphitheater',
        type: 'Outdoor Space',
        building_id: 'bld-15',
        building_name: 'Botanical Gardens',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Stage/Platform', 'Tiered Seating', 'Natural Light', 'Portable PA'],
        services: ['Venue Rentals', 'AV Setup', 'Catering Coordination', 'Photography Permitting'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: true,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // SAMUELI ENGINEERING (bld-16)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Boelter Hall 6750',
        display_name: 'Boelter Hall 6750',
        type: 'Classroom',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1500,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['AV & Tech Support', 'WiFi', 'Catering Coordination'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Boelter Hall Penthouse',
        display_name: 'Boelter Hall Penthouse',
        type: 'Event Space',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 60,
        sqft: 1000,
        features: ['Flexible Seating', 'Audio System', 'Natural Light', 'WiFi'],
        services: ['AV & Tech Support', 'WiFi', 'Catering Coordination'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Engineering VI Room 100',
        display_name: 'Engineering VI Room 100',
        type: 'Auditorium',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating', 'Video Conference'],
        services: ['AV & Tech Support', 'WiFi', 'Catering Coordination', 'Digital Signage Promotion'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Engineering VI Room 300',
        display_name: 'Engineering VI Room 300',
        type: 'Classroom',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 100,
        sqft: 1800,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['AV & Tech Support', 'WiFi'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Mong Learning Center',
        display_name: 'Mong Learning Center (Eng VI 180)',
        type: 'Classroom',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1500,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['AV & Tech Support', 'WiFi', 'Instructional Technology'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Cohen Multipurpose Room',
        display_name: 'Cohen Multipurpose Room (Eng VI 134)',
        type: 'Event Space',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 1800,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['AV & Tech Support', 'WiFi', 'Catering Coordination', 'Room Setup Support'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Engineering IV Patio',
        display_name: 'Engineering IV Patio',
        type: 'Outdoor Space',
        building_id: 'bld-16',
        building_name: 'Samueli Engineering',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Catering Coordination', 'Room Setup Support'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // LAW SCHOOL (bld-17)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Law School Lecture Hall',
        display_name: 'Law School Lecture Hall',
        type: 'Auditorium',
        building_id: 'bld-17',
        building_name: 'Law School',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Audio System', 'Projector', 'Tiered Seating', 'Video Conference', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi', 'Catering Coordination', 'Security & Safety Reviews', 'Custodial Services', 'Ticketing Services'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Law School Classroom A',
        display_name: 'Law School Classroom A',
        type: 'Classroom',
        building_id: 'bld-17',
        building_name: 'Law School',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1500,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Law School Meeting Room',
        display_name: 'Law School Meeting Room',
        type: 'Meeting Room',
        building_id: 'bld-17',
        building_name: 'Law School',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 30,
        sqft: 500,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Shapiro Courtyard',
        display_name: 'Shapiro Courtyard',
        type: 'Outdoor Space',
        building_id: 'bld-17',
        building_name: 'Law School',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 4000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light', 'Portable PA'],
        services: ['Venue Rentals', 'Catering Coordination', 'Event Planning Support', 'Security Coordination'],
        hourly_rate: 300,
        half_day_rate: 1800,
        full_day_rate: 3000,
        approval_required: true,
        catering_available: true,
        notes: 'Tables, easels, and ice buckets must be reserved through EMS.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // MOORE HALL (bld-18)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Moore Hall Reading Room',
        display_name: 'Moore Hall Reading Room',
        type: 'Event Space',
        building_id: 'bld-18',
        building_name: 'Moore Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 80,
        sqft: 1500,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Instructional Technology Support', 'Guest WiFi', 'Catering Coordination'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Moore Hall Classroom 100',
        display_name: 'Moore Hall Classroom 100',
        type: 'Classroom',
        building_id: 'bld-18',
        building_name: 'Moore Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1200,
        features: ['Projector', 'Audio System', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Instructional Technology Support', 'Guest WiFi'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Moore Hall Courtyard',
        display_name: 'Moore Hall Courtyard',
        type: 'Outdoor Space',
        building_id: 'bld-18',
        building_name: 'Moore Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 2000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 150,
        half_day_rate: 900,
        full_day_rate: 1500,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Moore Hall Auditorium',
        display_name: 'Moore Hall Auditorium',
        type: 'Auditorium',
        building_id: 'bld-18',
        building_name: 'Moore Hall',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 150,
        sqft: 2500,
        features: ['Audio System', 'Projector', 'Stage/Platform', 'Tiered Seating'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi', 'Catering Coordination'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: true,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // LUSKIN SCHOOL OF PUBLIC AFFAIRS (bld-19)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Luskin PA Classroom',
        display_name: 'Luskin Public Affairs Classroom',
        type: 'Classroom',
        building_id: 'bld-19',
        building_name: 'Luskin School of Public Affairs',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1200,
        features: ['Projector', 'Audio System', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi', 'Custodial & Setup Services'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: '3rd Floor Commons',
        display_name: 'Luskin 3rd Floor Commons',
        type: 'Event Space',
        building_id: 'bld-19',
        building_name: 'Luskin School of Public Affairs',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 100,
        sqft: 1800,
        features: ['Flexible Seating', 'Audio System', 'Projector', 'Catering Setup', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Guest WiFi', 'Catering Coordination', 'Event Planning & Logistics'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Luskin PA Patio',
        display_name: 'Luskin Patio',
        type: 'Outdoor Space',
        building_id: 'bld-19',
        building_name: 'Luskin School of Public Affairs',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 60,
        sqft: 1000,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Luskin PA Terrace',
        display_name: 'Luskin Terrace',
        type: 'Outdoor Space',
        building_id: 'bld-19',
        building_name: 'Luskin School of Public Affairs',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 80,
        sqft: 1500,
        features: ['Flexible Seating', 'Catering Setup', 'Natural Light'],
        services: ['Venue Rentals', 'Catering Coordination'],
        hourly_rate: 120,
        half_day_rate: 700,
        full_day_rate: 1200,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // POWELL LIBRARY / YRL (bld-20)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Powell Library Classroom A',
        display_name: '307A Powell Library (Classroom A)',
        type: 'Classroom',
        building_id: 'bld-20',
        building_name: 'Powell Library',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 800,
        features: ['Projector', 'Whiteboard', 'WiFi'],
        services: ['Guest WiFi', 'ADA Accessible Workstations'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
        notes: 'External events must have a campus sponsor.',
    },
    {
        name: 'Powell Library Classroom B',
        display_name: '320B Powell Library (Classroom B)',
        type: 'Classroom',
        building_id: 'bld-20',
        building_name: 'Powell Library',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 800,
        features: ['Projector', 'Whiteboard', 'WiFi'],
        services: ['Guest WiFi', 'ADA Accessible Workstations'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Powell Library Classroom C',
        display_name: '307C Powell Library (Classroom C)',
        type: 'Classroom',
        building_id: 'bld-20',
        building_name: 'Powell Library',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 800,
        features: ['Projector', 'Whiteboard', 'WiFi'],
        services: ['Guest WiFi', 'ADA Accessible Workstations'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Powell InqLab 3',
        display_name: '330 Powell Library (InqLab 3)',
        type: 'Classroom',
        building_id: 'bld-20',
        building_name: 'Powell Library',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 30,
        sqft: 600,
        features: ['Projector', 'Whiteboard', 'WiFi', 'Display Screen'],
        services: ['Guest WiFi', 'ADA Accessible Workstations', 'Technology Sandbox'],
        hourly_rate: 60,
        half_day_rate: 400,
        full_day_rate: 650,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'YRL RC Classroom',
        display_name: '11360F YRL (Research Commons Classroom)',
        type: 'Classroom',
        building_id: 'bld-20',
        building_name: 'Powell Library',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 30,
        sqft: 600,
        features: ['Projector', 'Display Screen', 'WiFi'],
        services: ['Guest WiFi', 'Data Science & GIS Stations', 'Equipment Lending'],
        hourly_rate: 60,
        half_day_rate: 400,
        full_day_rate: 650,
        approval_required: false,
        catering_available: false,
        notes: 'Located in Young Research Library Research Commons.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // DATAX (bld-21)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'DataX Impact Forum',
        display_name: 'Impact Forum',
        type: 'Event Space',
        building_id: 'bld-21',
        building_name: 'DataX',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 100,
        sqft: 1800,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Live-Streaming', 'Video Conference', 'WiFi'],
        services: ['Facility Services', 'Live-Streaming & Event Recording', 'Video Conferencing', 'Custom Room Layouts', 'Guest WiFi', 'Catering Coordination', 'Event Security'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'DataX Conference Room A',
        display_name: 'DataX Conference Room A',
        type: 'Conference Room',
        building_id: 'bld-21',
        building_name: 'DataX',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 20,
        sqft: 400,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Guest WiFi', 'Video Conferencing'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'DataX Collaboration Hub',
        display_name: 'Collaboration Hub',
        type: 'Event Space',
        building_id: 'bld-21',
        building_name: 'DataX',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 800,
        features: ['Flexible Seating', 'Display Screen', 'WiFi'],
        services: ['Guest WiFi', 'Custom Room Layouts'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
        notes: 'Open workspace configurable for events.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // KAPLAN HALL (bld-22)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Kaplan Hall 193',
        display_name: 'Kaplan Hall 193',
        type: 'Event Space',
        building_id: 'bld-22',
        building_name: 'Kaplan Hall',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 1200,
        features: ['Display Screen', 'Video Conference', 'Flexible Seating', 'WiFi'],
        services: ['Furniture (Chairs, Tables, Couches)', 'WiFi', 'Cleaning Services', 'AV Services', 'Catering Support'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: true,
        notes: 'Room size is 20\' x 60\'. Wall-mounted monitor, Zoom appliance, control podium.',
    },

    // ═══════════════════════════════════════════════════════════════════
    // LAKE ARROWHEAD LODGE (bld-23)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Lake Arrowhead Main Hall',
        display_name: 'Main Conference Hall',
        type: 'Event Space',
        building_id: 'bld-23',
        building_name: 'Lake Arrowhead Lodge',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 3000,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'Catering Setup', 'WiFi'],
        services: ['Built-in Sound Systems', 'Projectors & Flat Screen TVs', 'Video Conferencing', 'On-Site Technical Support', 'Award-Winning Dining', 'Complete Meeting Packages'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Lake Arrowhead Breakout Room A',
        display_name: 'Breakout Room A',
        type: 'Meeting Room',
        building_id: 'bld-23',
        building_name: 'Lake Arrowhead Lodge',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 30,
        sqft: 500,
        features: ['Display Screen', 'Whiteboard', 'WiFi'],
        services: ['Complimentary WiFi', 'On-Site Technical Support', 'Refreshment Breaks'],
        hourly_rate: 80,
        half_day_rate: 500,
        full_day_rate: 800,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Lake Arrowhead Breakout Room B',
        display_name: 'Breakout Room B',
        type: 'Meeting Room',
        building_id: 'bld-23',
        building_name: 'Lake Arrowhead Lodge',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 20,
        sqft: 400,
        features: ['Display Screen', 'Whiteboard', 'WiFi'],
        services: ['Complimentary WiFi', 'On-Site Technical Support', 'Refreshment Breaks'],
        hourly_rate: 60,
        half_day_rate: 400,
        full_day_rate: 650,
        approval_required: false,
        catering_available: true,
    },
    {
        name: 'Lake Arrowhead Boardroom',
        display_name: 'Executive Boardroom',
        type: 'Boardroom',
        building_id: 'bld-23',
        building_name: 'Lake Arrowhead Lodge',
        floor: 'Level 1',
        floor_index: 1,
        capacity: 16,
        sqft: 350,
        features: ['Large Display', 'Video Conference', 'Executive Seating', 'WiFi'],
        services: ['Complimentary WiFi', 'Video Conferencing', 'On-Site Bar & Lounge'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // UCLA RECREATION (bld-24)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Drake Stadium',
        display_name: 'Drake Stadium',
        type: 'Outdoor Space',
        building_id: 'bld-24',
        building_name: 'UCLA Recreation',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 3500,
        sqft: 50000,
        features: ['Stage/Platform', 'Audio System', 'Lighting Control', 'Natural Light'],
        services: ['Venue Rentals', 'Event Coordination', 'Security Coordination', 'Catering Coordination'],
        hourly_rate: 2000,
        half_day_rate: 10000,
        full_day_rate: 18000,
        approval_required: true,
        catering_available: true,
        notes: 'Outdoor large-scale events venue.',
    },
    {
        name: 'Los Angeles Tennis Center',
        display_name: 'Los Angeles Tennis Center & Clubhouse',
        type: 'Outdoor Space',
        building_id: 'bld-24',
        building_name: 'UCLA Recreation',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 5000,
        sqft: 80000,
        features: ['Stage/Platform', 'Audio System', 'Lighting Control', 'Natural Light'],
        services: ['Venue Rentals', 'Event Coordination', 'Security Coordination', 'Catering Coordination'],
        hourly_rate: 2500,
        half_day_rate: 12000,
        full_day_rate: 20000,
        approval_required: true,
        catering_available: true,
        notes: 'Used for ceremonies, concerts, and commencements.',
    },
    {
        name: 'Wooden Center Meeting Rooms',
        display_name: 'John Wooden Center Meeting Rooms',
        type: 'Meeting Room',
        building_id: 'bld-24',
        building_name: 'UCLA Recreation',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 40,
        sqft: 700,
        features: ['Display Screen', 'Video Conference', 'Whiteboard', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment'],
        hourly_rate: 100,
        half_day_rate: 600,
        full_day_rate: 1000,
        approval_required: false,
        catering_available: false,
    },
    {
        name: 'Sunset Canyon Recreation Center',
        display_name: 'Sunset Canyon Recreation Center',
        type: 'Event Space',
        building_id: 'bld-24',
        building_name: 'UCLA Recreation',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 300,
        sqft: 5000,
        features: ['Audio System', 'Flexible Seating', 'Catering Setup', 'Natural Light', 'WiFi'],
        services: ['Venue Rentals', 'Event Coordination', 'Catering Coordination'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Student Activities Center',
        display_name: 'Student Activities Center',
        type: 'Event Space',
        building_id: 'bld-24',
        building_name: 'UCLA Recreation',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 2500,
        features: ['Audio System', 'Projector', 'Flexible Seating', 'WiFi'],
        services: ['Venue Rentals', 'AV Equipment', 'Event Coordination'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: true,
    },

    // ═══════════════════════════════════════════════════════════════════
    // CAMPUS OUTDOOR SPACES (bld-25)
    // ═══════════════════════════════════════════════════════════════════
    {
        name: 'Wilson Plaza',
        display_name: 'Wilson Plaza',
        type: 'Outdoor Space',
        building_id: 'bld-25',
        building_name: 'Campus Outdoor Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 500,
        sqft: 15000,
        features: ['Flexible Seating', 'Portable PA', 'Natural Light', 'Catering Setup'],
        services: ['Event Coordination', 'Security Coordination', 'Catering Coordination'],
        hourly_rate: 500,
        half_day_rate: 3000,
        full_day_rate: 5000,
        approval_required: true,
        catering_available: true,
        notes: 'Suitable for receptions, ceremonies, concerts, and information fairs.',
    },
    {
        name: 'Court of Sciences',
        display_name: 'Court of Sciences',
        type: 'Outdoor Space',
        building_id: 'bld-25',
        building_name: 'Campus Outdoor Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 300,
        sqft: 8000,
        features: ['Flexible Seating', 'Portable PA', 'Natural Light', 'Catering Setup'],
        services: ['Event Coordination', 'Security Coordination', 'Catering Coordination'],
        hourly_rate: 400,
        half_day_rate: 2500,
        full_day_rate: 4000,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Franklin Murphy Sculpture Garden',
        display_name: 'Franklin Murphy Sculpture Garden',
        type: 'Outdoor Space',
        building_id: 'bld-25',
        building_name: 'Campus Outdoor Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 6000,
        features: ['Flexible Seating', 'Natural Light', 'Catering Setup'],
        services: ['Event Coordination', 'Photography Permitting', 'Catering Coordination'],
        hourly_rate: 350,
        half_day_rate: 2000,
        full_day_rate: 3500,
        approval_required: true,
        catering_available: true,
    },
    {
        name: 'Meyerhoff Park',
        display_name: 'Meyerhoff Park',
        type: 'Outdoor Space',
        building_id: 'bld-25',
        building_name: 'Campus Outdoor Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 200,
        sqft: 5000,
        features: ['Stage/Platform', 'Portable PA', 'Natural Light'],
        services: ['Event Coordination', 'Security Coordination'],
        hourly_rate: 250,
        half_day_rate: 1500,
        full_day_rate: 2500,
        approval_required: true,
        catering_available: false,
        notes: 'Free speech designated area with podium and amplified sound.',
    },
    {
        name: 'Dickson Courts',
        display_name: 'Dickson Courts North & South',
        type: 'Outdoor Space',
        building_id: 'bld-25',
        building_name: 'Campus Outdoor Spaces',
        floor: 'Ground Floor',
        floor_index: 0,
        capacity: 150,
        sqft: 4000,
        features: ['Flexible Seating', 'Natural Light'],
        services: ['Event Coordination'],
        hourly_rate: 200,
        half_day_rate: 1200,
        full_day_rate: 2000,
        approval_required: false,
        catering_available: false,
    },
];

// ── Generate spaces from UCLA room data ─────────────────────────────

const generateSpaceData = () => {
    return UCLA_ROOMS.map((room, index) => {
        const floorId = `${room.building_id}_lvl-${floorIndexToLevelCode(room.floor_index)}`;
        const room_code = `${room.building_id.split('-')[1]}.${room.floor_index}.${String(index + 1).padStart(2, '0')}`;

        return {
            id: `space-${index + 1}`,
            name: `${room_code} ${room.name}`,
            display_name: room.display_name,
            map_id: `area-${room_code.toLowerCase().replace(/\./g, '-')}-status`,
            zones: getZoneHierarchy(room.building_id, floorId),
            type: room.type,
            code: room.type,
            capacity: room.capacity,
            features: room.features,
            images: [
                `https://images.unsplash.com/photo-${1500000000 + index * 100000}?w=800&h=600&fit=crop`,
            ],
            bookable: true,
            approval: room.approval_required,
            level: {
                id: floorId,
                parent_id: room.building_id,
                name: room.floor,
                display_name: room.floor,
                capacity: room.capacity,
                number: room.floor_index.toString(),
                map_id: `${room.building_id}_${room.floor_index}`,
                tags: [room.type.toLowerCase().replace(/\s+/g, '-')],
                settings: {
                    booking_enabled: true,
                    auto_release: room.type === 'Meeting Room' || room.type === 'Conference Room',
                    max_booking_duration: room.type === 'Auditorium' || room.type === 'Ballroom' ? 720 : 480,
                },
                images: [
                    `https://images.unsplash.com/photo-${1400000000 + room.floor_index * 200000}?w=1200&h=800&fit=crop`,
                ],
                locations: {
                    id: floorId,
                    name: room.floor,
                    building: room.building_name,
                },
            },
            building: room.building_name,
            building_id: room.building_id,
            floor: room.floor,
            floor_number: room.floor_index,
            room_code,
            sqft: room.sqft,
            services: room.services,
            notes: room.notes || '',
            accessibility: {
                wheelchair_accessible: true,
                hearing_loop: ['Auditorium', 'Ballroom', 'Theater'].includes(room.type),
                braille_signage: true,
            },
            equipment: room.features.map((feature) => ({
                name: feature,
                status: 'Working',
                last_serviced: new Date(
                    Date.now() - 30 * 24 * 60 * 60 * 1000,
                ).toISOString(),
            })),
            amenities: {
                natural_light: room.type === 'Outdoor Space' || room.features.includes('Natural Light'),
                air_conditioning: room.type !== 'Outdoor Space',
                power_outlets: Math.max(room.capacity, 10),
                wifi: true,
                catering_available: room.catering_available,
                parking_nearby: true,
            },
            booking_rules: {
                advance_booking_days: room.approval_required ? 90 : 30,
                min_booking_duration: 60,
                max_booking_duration: room.type === 'Auditorium' || room.type === 'Ballroom' ? 720 : 480,
                buffer_time: room.type === 'Event Space' || room.type === 'Ballroom' ? 60 : 30,
                approval_required: room.approval_required,
            },
            cost_center: room.building_name.toLowerCase().replace(/\s+/g, '-'),
            hourly_rate: room.hourly_rate,
            half_day_rate: room.half_day_rate || 0,
            full_day_rate: room.full_day_rate || 0,
        };
    });
};

const rawSpaces = generateSpaceData();

export const MOCK_SPACES = rawSpaces.map((space) =>
    generateMockSpace({
        ...space,
        features: space.features || [],
        images: space.images || [
            'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
        ],
    }),
);

// Helper functions for space management
export const getSpacesByType = (type: string) =>
    MOCK_SPACES.filter((space) => space.type === type);

export const getSpacesByBuilding = (buildingId: string) =>
    MOCK_SPACES.filter((space) => space.building_id === buildingId);

export const getSpacesByCapacity = (
    minCapacity: number,
    maxCapacity?: number,
) =>
    MOCK_SPACES.filter((space) => {
        const capacity = space.capacity || 0;
        return (
            capacity >= minCapacity && (!maxCapacity || capacity <= maxCapacity)
        );
    });

export const getBookableSpaces = () =>
    MOCK_SPACES.filter((space) => space.bookable !== false);

export const getSpacesByFeature = (feature: string) =>
    MOCK_SPACES.filter(
        (space) => space.features && space.features.includes(feature),
    );

export const getAccessibleSpaces = () =>
    MOCK_SPACES.filter((space) => space.accessibility?.wheelchair_accessible);

export const getSpacesRequiringApproval = () =>
    MOCK_SPACES.filter((space) => space.booking_rules?.approval_required);
